import { call, put, takeLatest } from '@redux-saga/core/effects';
import {default as _axios, AxiosRequestConfig} from 'axios';
import config from '../../../config/config';
import { getOAuthCredentials, isWebView } from '../../utilities/webview';
import {
  checkPhoneExistFailedAction,
  checkPhoneExistSuccessAction,
  forgotPasswordPINFailedAction,
  forgotPasswordPINSuccessAction,
  requestCreateBizAccountFailed,
  requestCreateBizAccountSuccess,
  requestGetProfileFailed,
  requestGetProfileSuccess,
  requestLocationSuccess,
  requestLoginFailedAction,
  requestLoginSuccessAction,
  requestNewOTPActionFailed,
  resetStateAction,
  updatePasswordPINActionSuccess,
  verifyPhoneCodeFailedAction,
  verifyPhoneCodeSuccessAction
} from './actions';
import {
  ACCOUNT_LOCK,
  CHECK_PHONE_EXISTS,
  CREATE_BIZ_ACCOUNT,
  FORGOT_PASSWORD_PIN,
  GET_LOCATION,
  GET_PROFILE_BIZ_CUSTOMER,
  REQUEST_LOGIN,
  REQUEST_NEW_OTP_CODE,
  UPDATE_BIZ_ACCOUNT_PASSWORD,
  UPDATE_PASSWORD_PIN,
  UPDATE_PASSWORD_PIN_SUCCESS,
  VERIFY_CODE
} from './actionTypes';
import {
  AuthLogin,
  AuthPhoneNumber,
  AuthUpdatePasswordBizAccountPayload,
  AuthUpdatePasswordPINPayload,
  AuthVerifyCode,
  businessAccountPayload
} from './types';
// @ts-ignore
import { cookieStorage } from '@swift-247/s247.library.ui-core';

import { BrowserStorageUtil } from '@swift-247/s247.library.ui-core';
import buildTimeConfig from '../../../config/buildtime.config';
import { getReCaptchaToken } from '../../utilities';
import { trackEvent } from '../../utilities/ga';

const isLiveEnvironment = buildTimeConfig.env !== 'local';
const envStorage = isLiveEnvironment ? localStorage : cookieStorage;
const browserStorageUtil = new BrowserStorageUtil(envStorage as any);

const captchaInjectionMiddleware = async (req: AxiosRequestConfig) => {
    const injectedPaths = ['/v1/check_phone', '/v1/request_new_otp'];
    if(!injectedPaths.find(path => req.url?.includes(path))) return req;

    const captchaToken = await getReCaptchaToken() as string | undefined;
    if(!captchaToken) return req;

    req.data = {
        recaptcha_response: captchaToken,
        ...req.data,
    }

    return req;
}

const axios = _axios.create({});

axios.interceptors.request.use(
    function (req: AxiosRequestConfig) {
        return captchaInjectionMiddleware(req);
    },
    function (error) {
        return Promise.reject(error);
    },
);

// In a partner webview (e.g. Vikki), skip the reCAPTCHA-gated endpoints — the
// widget doesn't reliably render/verify inside embedded webviews — and use the
// app247 counterparts instead (same backend behavior, no reCAPTCHA requirement).
const SCheckPhoneExist = (phone: AuthPhoneNumber) =>
  axios.post<AuthPhoneNumber>(
    `${config.mainURL}/v1/${isWebView() ? 'app247/check_phone' : 'check_phone'}`,
    phone
  );

const SRequestNewOTP = (phone: AuthPhoneNumber) =>
  axios.post<AuthPhoneNumber>(
    `${config.mainURL}/v1/${isWebView() ? 'app247/request_new_otp' : 'request_new_otp'}`,
    phone
  );

const SVerifyCode = (payload: AuthVerifyCode) =>
  axios.post<AuthVerifyCode>(`${config.mainURL}/v1/validate_phone`, {
    phone: payload.phone,
    otp: payload.code,
  });

const SUpdatePasswordPin = (payload: AuthUpdatePasswordPINPayload) =>
  axios.post<AuthUpdatePasswordPINPayload>(`${config.mainURL}/v1/update_pin`, {
    phone: payload.phone,
    user_name: payload.user_name,
    pin: payload.pin,
    verified_token: payload.verified_token,
  });

const SUpdateBizAccountPassword = (
  payload: AuthUpdatePasswordBizAccountPayload,
) => {
  const authInfo = browserStorageUtil.deserializedGet('authInfo');

  const { accessToken } = authInfo;
  const configHeader = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };
  axios.put<AuthUpdatePasswordBizAccountPayload>(
    `${config.mainURL}/v1/user/password`,
    {
      old_password: payload.old_password,
      new_password: payload.new_password,
    },
    configHeader,
  );
};

const SGetLocation = (locId: string) =>
  axios.get<AuthPhoneNumber>(`${config.mainURL}/v2/get_location`, {
    params: {
      locId,
    },
  });

const SForgotPasswordPin = (phone: AuthPhoneNumber) =>
  axios.post<AuthPhoneNumber>(`${config.mainURL}/v1/password/forgot`, phone);

const SCreateBizAccount = (payload: businessAccountPayload) =>
  axios.post<businessAccountPayload>(
    `${config.mainURL}/v1/create_business_account`,
    payload,
  );

const SGetProfile = () => {
  const authInfo = browserStorageUtil.deserializedGet('authInfo');

  const { accessToken } = authInfo;
  const configHeader = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };
  return axios.get(`${config.mainURL}/v1/user/profile`, configHeader);
};

const SLogin = (payload: AuthLogin) =>
  axios.post<AuthVerifyCode>(`${config.mainURL}/v1/oauth/token`, {
    username: payload.username,
    password: payload.password,
    client_id: payload.client_id,
    client_secret: payload.client_secret,
    grant_type: payload.grant_type,
  });

function* checkPhoneSaga(param: AuthPhoneNumber): any {
  try {
    const res = yield call(SCheckPhoneExist, { phone: param.phone });
		if (res.status === 200) {
			let message = '';
			if (res.data.data.status === ACCOUNT_LOCK) {
				message = 'customer_code_has_been_disabled_please_contact_the_hotline_for_assistance';
			} else if (res.data.data.status === 'NONE' && res.data.data.customer_code) {
				message = 'validation_valid_customer_code';
			}

      yield put(
        checkPhoneExistSuccessAction({
          phone: res.data.data.phone,
          customerCode: res.data.data.customer_code,
          status: res.data.data.status,
          message,
        }),
      );
    } else {
      yield put(
        checkPhoneExistFailedAction({
          message: res.statusText,
          status: res.status,
        }),
      );
    }
  } catch (e: any) {
    // A genuine network-level failure (no HTTP response received at all —
    // connection reset, timeout, or the browser hiding a response that's
    // missing CORS headers) leaves e.response undefined; reading
    // e.response.data straight away crashed here with a second, unrelated
    // TypeError instead of ever reaching checkPhoneExistFailedAction, so the
    // saga silently died and the user only ever saw axios's raw generic
    // "Network Error" (e.message) with no real failure message shown.
    let message = e.response?.data?.message ?? e.message;
    if (e.response?.data?.errors?.phone) {
      message = (e.response.data.errors.phone[0])?.toLowerCase();
    }
    if(message && message.includes('reCAPTCHA')) {
        message = 'validation_recaptcha'
    }
    yield put(
      checkPhoneExistFailedAction({
        message,
        status: e.response?.data?.status,
      }),
    );
  }
}

// call request new otp code
function* onRequestNewOtpCodeSaga(param: AuthPhoneNumber): any {
  try {
    const res = yield call(SRequestNewOTP, { phone: param.phone });
    trackEvent('request_otp');
    // yield put(requestNewOTPActionFailed(res.data));
    // yield put(push('/authorize/verify-code'));
    // alert('Đã gửi lại mã thành công. Vui lòng kiểm tra tin nhắn');
  } catch (err: any) {
    // const errorJson = yield err.json();
    let message = err.response.data.message;
      if(message && message.includes('reCAPTCHA')) {
          message = 'validation_recaptcha'
      }
    if (
      err.response &&
      err.response.data &&
      err.response.data.code &&
      err.response.data.code === 'uranus.error.sms.max_exceed'
    ) {
      yield put(
        requestNewOTPActionFailed(
            message || 'Tiếc quá! Đã vượt qua số lượt gửi OTP trong ngày',
        ),
      );
    }
  }
}

function* requestVerifyCodeActionSaga(param: AuthVerifyCode): any {
  try {
    const res = yield call(SVerifyCode, {
      phone: param.phone,
      code: param.code,
    });
    if (res.data.data.is_valid) {
      trackEvent('verify_otp_success');
      yield put(
        verifyPhoneCodeSuccessAction({
          isValid: res.data.data.is_valid,
          token: res.data.data.token,
        }),
      );
    } else {
      trackEvent('verify_otp_failed', { reason: 'wrong_otp' });
      yield put(
        verifyPhoneCodeFailedAction({
          isValid: false,
          token: false,
          message: 'validation_wrong_otp'
        }),
      );
    }
  } catch (err: any) {
    const code = err.response.data.code;
    let msg = '';
    if (code === 'uranus.error.auth.otp_max_3_times') {
      msg = 'validation_wrong_otp_many_times';
    }
    trackEvent('verify_otp_failed', { reason: code });
    yield put(
      verifyPhoneCodeFailedAction({
        isValid: false,
        token: false,
        message: msg,
      }),
    );
  }
}

function* onUpdatePasswordPINSaga(param: AuthUpdatePasswordPINPayload): any {
  try {
    const res = yield call(SUpdatePasswordPin, {
      phone: param.phone,
      user_name: param.user_name,
      pin: param.pin,
      verified_token: param.verified_token,
    });

    if (res.data.data) {
      yield put(
        updatePasswordPINActionSuccess({
          type: UPDATE_PASSWORD_PIN_SUCCESS,
          phone: '',
          status: '',
          message: '',
          errorCode: '',
          code: '',
          isValid: false,
          token: '',
          isLoggedIn: false,
          accessToken: '',
          expiresIn: 0,
          refreshToken: '',
          tokenType: '',
          canForgotPassword: null,
          customerCode: '',
          profile: undefined,
        }),
      );
    }
  } catch (err) {}
}

function* onUpdateBizAccountPasswordSaga(
  param: AuthUpdatePasswordBizAccountPayload,
): any {
  try {
    const res = yield call(SUpdateBizAccountPassword, {
      old_password: param.old_password,
      new_password: param.new_password,
    });
		if (res.data.status === 'success') {
			yield put(resetStateAction());
		}
	} catch (err) {

	}
}

function* onForgotPasswordPINSaga(param: AuthPhoneNumber): any {
  try {
    const res = yield call(SForgotPasswordPin, { phone: param.phone });

    yield put(forgotPasswordPINSuccessAction(res));
  } catch (err: any) {
    switch (err.response.data.code) {
      case 'uranus.error.sms.max_exceed':
        yield put(
          forgotPasswordPINFailedAction({
            message: 'validation_wrong_otp_quota',
            errorCode: ''
          }),
        );
        // alert(
        //   'Bạn đã đạt đến số lần lấy lại mật khẩu trong hôm nay. Vui lòng thử lại sau.',
        // );
        break;
      case 'uranus.error.sms.in_range_time':
        yield put(
          forgotPasswordPINFailedAction({
            message: 'validation_wrong_otp_quota_per_min',
            errorCode: ''
          }),
        );
        // alert(
        //   'Bạn đang yêu cầu lấy lại mật khẩu quá 2 lần trong vòng 1 phút. Vui lòng thử lại sau',
        // );
        break;
      default:
    }
  }
}

function* requestLoginActionSaga(param: AuthLogin): any {
  try {
    const res = yield call(SLogin, {
      username: param.username,
      password: param.password,
      client_id: getOAuthCredentials().clientId,
      client_secret: getOAuthCredentials().clientSecret,
      grant_type: 'password',
    });
    if (res.status === 200) {
      trackEvent('login_success');
      yield put(
        requestLoginSuccessAction({
          isLoggedIn: true,
          accessToken: res.data.access_token,
          expiresIn: res.data.expires_in,
          refreshToken: res.data.refresh_token,
          tokenType: res.data.token_type,
          status: '',
        }),
      );
    } else {
      trackEvent('login_failed', { error_code: res.data.error });
      yield put(
        requestLoginFailedAction({
          message: res.data.message,
          errorCode: res.data.error,
        }),
      );
    }
  } catch (e: any) {
    // status = 401, login failed
    trackEvent('login_failed', { error_code: e.response.data.error });
    yield put(
      requestLoginFailedAction({
        message: e.response.data.message,
        errorCode: e.response.data.error,
      }),
    );
  }
}

function* requestGetProfileSaga(): any {
  try {
    const res = yield call(SGetProfile);
    if (res.status === 200) {
      yield put(
        requestGetProfileSuccess({
          address: res.data.data.address,
          avatar: res.data.data.avatar,
          birth: res.data.data.birth,
          companyName: res.data.data.company_name,
          companyPhone: res.data.data.company_phone,
          companyRepresentative: res.data.data.company_representative,
          confirmed: res.data.data.confirmed,
          country: res.data.data.country,
          createdAt: res.data.data.created_at,
          customerCode: res.data.data.customer_code,
          district: res.data.data.district,
          email: res.data.data.email,
          gender: res.data.data.gender,
          id: res.data.data.id,
          idenDate: res.data.data.iden_date,
          idenNumber: res.data.data.iden_number,
          idenPlace: res.data.data.iden_place,
          isShowInstruction: res.data.data.is_show_instruction,
          linkedAccount: res.data.data.linked_account,
          loginFirst: res.data.data.login_first,
          mixId: res.data.data.mix_id,
          name: res.data.data.name,
          nickname: res.data.data.nickname,
          object: res.data.data.object,
          phone: res.data.data.phone,
          province: res.data.data.province,
          readableCreatedAt: res.data.data.readable_created_at,
          readableUpdatedAt: res.data.data.readable_updated_at,
          taxNumber: res.data.data.tax_number,
          updatedAt: res.data.data.updated_at,
          village: res.data.data.village,
        }),
      );
    }
  } catch (e: any) {
    // status = 401, login failed
    yield put(
      requestGetProfileFailed({
        message: e.response.data.message,
        errorCode: e.response.data.error,
      }),
    );
  }
}

function* onGetLocationSaga(payload: {
  locationType: 'country' | 'province' | 'district' | 'ward';
  id: string;
}): any {
  try {
    const res = yield call(SGetLocation, payload.id);
    yield put(
      requestLocationSuccess({
        locationType: payload.locationType,
        data: res.data,
      }),
    );
  } catch (err: any) {}
}

function* requestCreateBusinessAccount(payload: {
  data: businessAccountPayload;
}): any {
	try {
    const res = yield call(SCreateBizAccount, { ...payload.data });
    trackEvent('create_account_success');
    yield put(requestCreateBizAccountSuccess(res.data));
  } catch (err: any) {
    trackEvent('create_account_failed', { error: err.response?.data?.errors });
    yield put(requestCreateBizAccountFailed(err.response?.data?.errors));
  }
}

function* authSaga() {
  yield takeLatest(CHECK_PHONE_EXISTS, checkPhoneSaga);
  yield takeLatest(REQUEST_NEW_OTP_CODE, onRequestNewOtpCodeSaga);
  yield takeLatest(VERIFY_CODE, requestVerifyCodeActionSaga);
  yield takeLatest(REQUEST_LOGIN, requestLoginActionSaga);
  yield takeLatest(UPDATE_PASSWORD_PIN, onUpdatePasswordPINSaga);
  yield takeLatest(FORGOT_PASSWORD_PIN, onForgotPasswordPINSaga);
  yield takeLatest(GET_PROFILE_BIZ_CUSTOMER, requestGetProfileSaga);
  yield takeLatest(UPDATE_BIZ_ACCOUNT_PASSWORD, onUpdateBizAccountPasswordSaga);
  yield takeLatest(GET_LOCATION, onGetLocationSaga);
  yield takeLatest(CREATE_BIZ_ACCOUNT, requestCreateBusinessAccount);
}
export default authSaga;
